import {Component, OnInit, ViewChild} from '@angular/core';
import {TextUtils} from "../../../../../shared/utils/text-utils";
import {ActivatedRoute} from "@angular/router";
import {OrdersService} from "../orders.service";
import {IOrder} from "../orders.interface";
import {IEcommerceView} from "../../ecommerce-product.interface";
import {DatePipe} from "@angular/common";
import {Store} from "@ngrx/store";
import * as fromStore from "../../../../../store";
import {filter, first} from "rxjs/operators";
import {EcommerceService} from "../../ecommerce.service";
import {EcommerceAnalyticsLabel} from "../../ecommerce-product.enum";
import {RefundItemsPopupComponent} from "../refund-items-popup/refund-items-popup.component";
import {DbModalService} from "../../../../../modules/modal/modal";
import {Utils} from "../../../../../shared/utils/utils";
import {RefundDataMapper} from "../mapper/refund-data-mapper";
import {RefundData} from "../mapper/refund-data";
import {DbNumberFormatPipe} from "../../../../../shared/pipes/db-number-ceil.pipe";
import {combineLatest} from "rxjs";

@Component({
    selector: 'db-order-view',
    templateUrl: './order-view.component.html',
    styleUrls: ['./order-view.component.scss'],
    standalone: false
})
export class OrderViewComponent implements OnInit {
  headerGreedStyle = {
    'grid-template-columns': '76px minmax(150px, 1fr) minmax(70px, 1fr) minmax(70px, 1fr) minmax(70px, 1fr)'
  };
  headerGreedStyleRefund = {
    'grid-template-columns': '76px minmax(174px, 1fr) minmax(174px, 1fr) minmax(50px, 1fr) 84px',
    'margin-left': '40px'
  };
  rowGreedStyleRefund = {
    'grid-template-columns': '16px 76px minmax(174px, 1fr) minmax(174px, 1fr) minmax(50px, 1fr) 84px'
  };
  orderId = this._activatedRoute.snapshot.params['orderId'];
  website = this._activatedRoute.snapshot.parent.parent.parent.data['website'];
  order: IOrder;
  view: IEcommerceView;
  loading = true;
  heads = [
    TextUtils.getText('text_image'),
    TextUtils.getText('text_product'),
    TextUtils.getText('text_quantity'),
    TextUtils.getText('text_total'),
    TextUtils.getText('text_tax')
  ];
  customer: {} & { date_created: { date: string }, email: string, customer_id: number };
  refundedItemsInfo: any[] = [];
  refunds: RefundData[];
  Math: any = Math;
  refundsTotalSum: number = 0;
  halfUp = new DbNumberFormatPipe();
  refundedShippingAmount: number = 0;
  refundedShippingTaxAmount: number = 0;

  private _analyticsActionName = 'Ecommerce - Orders';

  @ViewChild(RefundItemsPopupComponent) refundItemsPopup: RefundItemsPopupComponent;

  constructor(private _activatedRoute: ActivatedRoute,
              private _datePipe: DatePipe,
              private _store: Store<fromStore.DbStoreState>,
              private _ecommerceService: EcommerceService,
              private _ordersService: OrdersService,
              private _modalService: DbModalService) {
  }

  ngOnInit() {
    this.refundedShippingAmount = 0;
    this.refundedShippingTaxAmount = 0;
    this._getOrder();
    this._getOrderFiledListener();
    this._store.dispatch(new fromStore.LoadEcommerceOrder({websiteId: this.website.id, id: this.orderId}));
    this._store.dispatch(new fromStore.LoadEcommerceRefunds({websiteId: this.website.id, orderId: this.orderId}));
  }

  private _getOrder() {
    combineLatest(
      this._store.select(fromStore.getEcommerceRefunds(this.website?.id, this.order?.id)),
      this._store.select(fromStore.getOrder(this.website?.id))
    )
      .pipe(
        filter(([refunds, order]) => {
          if (order) {
            if (Array.isArray(order.order) && Array.isArray(order.customer)) {
              this._ecommerceService.sendAnalyticsEvent(this._analyticsActionName, EcommerceAnalyticsLabel.VIEW, true);
            }
          }

          return !!order;
        })
      ).subscribe(([refundsData, response]) => {
      let shippingRefundsTotalSum = 0.00;
      let shippingTaxRefundsTotalSum = 0.00;
      refundsData.forEach(refund  => {
        refund.shipping_lines.forEach(shippingLine => {
          shippingRefundsTotalSum += this.halfUp.transform(shippingLine.total, 'halfUp', 2);
          shippingTaxRefundsTotalSum += this.halfUp.transform(shippingLine.total_tax, 'halfUp', 2);
        });
      });

      this.refundedShippingAmount = Math.abs(shippingRefundsTotalSum);
      this.refundedShippingTaxAmount = Math.abs(shippingTaxRefundsTotalSum);

      this.refunds = response?.refunds.map(refund => RefundDataMapper.map(refund));
      this.order = response?.order;
      this.order.hasTestEnvironment = response?.order?.meta_data?.some(item => item.key === 'twwp_environment' && item.value === 'test');
      const combinedLineItems = this.refunds.flatMap(order => order.lineItems);
      const combinedRefunds = this._combineRefundLineItems(combinedLineItems);
      this.order.line_items = this._combinedRefundsData(this.order.line_items, combinedRefunds);
      this.customer = response?.customer;
      this.view = this._transformToView(this.order);
      this.loading = false;
      this._ecommerceService.sendAnalyticsEvent(this._analyticsActionName, EcommerceAnalyticsLabel.VIEW);
    });
  }

  private _combineRefundLineItems(items) {
    const combinedItems = items.reduce((acc, item) => {
      // Find if the item already exists in the accumulator
      const existingItem = acc.find(i => (i.product_id === item.product_id && i.variation_id === item.variation_id));

      if (existingItem) {
        // Sum the values if the item already exists
        existingItem.quantity += item.quantity;
        existingItem.total = (parseFloat(existingItem.total) + parseFloat(item.total));
        existingItem.total_tax = (parseFloat(existingItem.total_tax) + parseFloat(item.total_tax)).toFixed(2);
      } else {
        // If it doesn't exist, use a parsed version of total and total_tax
        acc.push({
          product_id: +item?.product_id,
          variation_id: +item?.variation_id,
          quantity: +item?.quantity,
          total: parseFloat(item.total).toFixed(2),
          total_tax: parseFloat(item.total_tax).toFixed(2)
        });
      }
      return acc;
    }, []);
    return combinedItems;
  }

  private _combinedRefundsData(line_items, refunds): any {
    const combinedData = line_items.map(item => {
      const refund = refunds.find(refund => (refund?.product_id === item?.product_id && refund?.variation_id === item?.variation_id));
      return {
        ...item,
        refund_details: refund ? refund : null
      };
    });
    return combinedData;
  }

  private _getOrderFiledListener() {
    this._store.select(fromStore.getOrderFiled(this.website.id))
      .pipe(filter(filed => filed), first())
      .subscribe(() => {
        this._ecommerceService.sendAnalyticsEvent(this._analyticsActionName, EcommerceAnalyticsLabel.VIEW, true);
      });
  }

  private _transformToView(order: IOrder): IEcommerceView {
    const view: Partial<IEcommerceView> = {
      header: {
        previousPage: TextUtils.getText('text_orders'),
        currentPage: TextUtils.getText('text_orderView'),
        previousPath: "../orders"
      }
    };
    view.stats = {} as any;
    view.stats.header = {
      date: this.customer.date_created ? `${TextUtils.getText('text_customerSince')} ${this._datePipe.transform(this.customer.date_created.date, 'MMM d, y')}` : '',
      name: this.customer.email,
    };

    const stat = {
      value: order?.billing?.email,
      userId: this.customer.customer_id,
      label: TextUtils.getText('text_user')
    };

    const stat1 = {
      value: this._datePipe.transform(order && order.date_created, 'MMM d, y'),
      label: TextUtils.getText('text_orderDate')
    };
    const stat2 = {
      value: order.line_items.length.toString(),
      label: TextUtils.getText('text_ordersQuantity')
    };
    const stat3 = {
      value: order.transaction_id,
      label: TextUtils.getText('text_transactionId')
    };
    const stat4 = {
      value: Utils.capitalize(order.status),
      label: TextUtils.getText('text_orderStatus')
    };

    let refundsTotalSum = 0;
    order?.refunds.forEach(item => {
      refundsTotalSum += parseFloat(item?.total);
    });

    this.refundsTotalSum = Math.abs(refundsTotalSum);

    let subTotals = 0;
    order?.line_items.forEach(item => {
      subTotals += parseFloat(item?.subtotal);
    });

    view.stats.stats = [stat, stat1, stat2, stat3, stat4];

    view.sidebar = {
      header: TextUtils.getText('text_shippingRefund'),
      items: [
        {
          label: TextUtils.getText('text_subtotal'),
          value: `${order.currency_symbol}${subTotals.toFixed(2)}`
        },
        {
          label: TextUtils.getText('text_discount'),
          value: `-${order.currency_symbol}${order.discount_total}`
        },
        {
          label: TextUtils.getText('text_shipping'),
          value: `${order.currency_symbol}${(+order.shipping_total - this.refundedShippingAmount).toFixed(2)}`
        },
        {
          label: TextUtils.getText('text_tax'),
          value: `${order.currency_symbol}${order.total_tax}`
        },
        {
          label: TextUtils.getText('text_shippingTax'),
          value: `${order.currency_symbol}${(+order.shipping_tax - this.refundedShippingTaxAmount).toFixed(2)}`
        }
      ],
      footer: {
        label: !!refundsTotalSum ? TextUtils.getText('text_netPayment') : TextUtils.getText('text_total'),
        value: `${order.currency_symbol}${!!refundsTotalSum ? (parseFloat(order.total) + refundsTotalSum).toFixed(2) : order.total}`
      },
      orderTotal: {
        label: TextUtils.getText('text_orderTotal'),
        value: `${order.currency_symbol}${order.total}`
      },
      paid: {
        label: TextUtils.getText('text_paid'),
        value: `${order.currency_symbol}${order.total}`
      },
      refunded: {
        label: TextUtils.getText('text_refunded'),
        value: `-${order.currency_symbol}${Math.abs(this.halfUp.transform(refundsTotalSum, 'halfUp', 2))}`
      },
      hasRefunds: {
        label: TextUtils.getText('text_refunded'),
        value: !!refundsTotalSum
      }
    };

    return view as IEcommerceView;
  }

  getAttributes(meta: any[]) {
    if (!meta) {
      return '';
    }
    return meta.filter(item => item.key !== "_reduced_stock" && item.value.length).map((item) => item.value).join(', ');
  }

  handleRefundSectionToggle([show, fullRefund]) {
    const modal = this._modalService.open(RefundItemsPopupComponent, {height: 600, width: 1240});
    modal.componentInstance.order = this.order;
    modal.componentInstance.refunds = this.refunds;
    modal.componentInstance.fullRefund = fullRefund;
    modal.componentInstance.websiteId = this.website.id;
    modal.componentInstance.getAttributes = this.getAttributes.bind(this);

    modal.componentInstance.onRefundSuccess = () => {
      this._store.dispatch(new fromStore.LoadEcommerceOrders({websiteId: this.website.id}));
      this._store.dispatch(new fromStore.LoadEcommerceOrder({websiteId: this.website.id, id: this.orderId}));
      console.log("Dispatched action to reload order data");
    };
  }


}
